import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { useNodeDuplication } from '@/hooks/useNodeDuplication';
import { I18N_KEYS } from '@/language/keys';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useFlowView } from '../Recipe/FlowContext';
import type { NodeId } from 'web';
import type { DialogData } from '@/state/global.state';

interface NodeActions {
  deleteNode: (nodeId: NodeId) => void;
  deleteNodes: (nodeIds: NodeId[], skipDialog?: boolean) => void;
  deleteSelected: () => void;
  duplicateNode: (nodeId: NodeId) => void;
  duplicateNodes: (nodeIds: NodeId[]) => void;
  toggleLockNode: (nodeId: NodeId) => void;
  openOverlayDialog: (dialogData: DialogData) => void;
  closeOverlayDialog: () => void;
  setCoverImage: (poster: string) => Promise<void>;
}

export function useNodeActions(): NodeActions {
  const flowView = useFlowView();
  const { positionDuplicatedNodes, scrollNodeIntoViewIfNeeded } = useNodeDuplication();
  const { t } = useTranslation();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const openOverlayDialog = useGlobalStore((s) => s.openDialog);
  const closeOverlayDialog = useGlobalStore((s) => s.closeDialog);
  const { getItem: getAllowDeleteGroupWithoutWarning } = useLocalStorage<boolean>('allowDeleteGroupWithoutWarning');

  const deleteNodes = useCallback(
    (ids: NodeId[], skipDialog: boolean = false) => {
      if (!skipDialog && !getAllowDeleteGroupWithoutWarning()) {
        const groupIds = ids.filter((id) => flowView.isGroupNode(id));

        if (groupIds.length > 0) {
          openOverlayDialog({
            type: 'deleteGroup',
            allNodeIdsToDelete: ids,
          });
          return;
        }
      }

      const result = flowView.deleteNodes(ids);

      if (result.preventedGroups && result.preventedGroups.length > 0) {
        updateSnackbarData({
          text: t(I18N_KEYS.RECIPE_MAIN.FLOW.CANNOT_DELETE_GROUP_WITH_LOCKED_NODES),
          severity: 'warning',
          icon: null,
          isOpen: true,
        });
      }
    },
    [flowView, t, updateSnackbarData, openOverlayDialog, getAllowDeleteGroupWithoutWarning],
  );

  const deleteNode = useCallback((id: NodeId) => deleteNodes([id]), [deleteNodes]);

  const deleteSelected = useCallback(() => {
    if (flowView.selectedNodes.length > 0) {
      deleteNodes(flowView.selectedNodes);
    } else if (flowView.selectedEdges.length > 0) {
      flowView.deleteEdges(flowView.selectedEdges);
    }
  }, [flowView, deleteNodes]);

  const duplicateNode = useCallback(
    (id: NodeId) => flowView.duplicateNodes([id], positionDuplicatedNodes, scrollNodeIntoViewIfNeeded),
    [flowView, positionDuplicatedNodes, scrollNodeIntoViewIfNeeded],
  );
  const duplicateNodes = useCallback(
    (nodeIds: NodeId[]) => flowView.duplicateNodes(nodeIds, positionDuplicatedNodes, scrollNodeIntoViewIfNeeded),
    [flowView, positionDuplicatedNodes, scrollNodeIntoViewIfNeeded],
  );
  const toggleLockNode = useCallback((id: NodeId) => flowView.toggleLockNode(id), [flowView]);
  const setCoverImage = useWorkflowStore((s) => s.setRecipePoster);

  return {
    deleteNode,
    deleteNodes,
    deleteSelected,
    duplicateNode,
    duplicateNodes,
    toggleLockNode,
    openOverlayDialog,
    closeOverlayDialog,
    setCoverImage,
  };
}
